import type { ReactNode } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { HOTSPOTS, MODULES, t } from '../content/content'
import { useKiosk } from '../state/store'
import { productBijModel } from '../producten/catalogus'
import { ProductsheetQR } from '../producten/ProductsheetQR'
import { FilmKnop, VEER } from './gedeeld'

/**
 * Automatisering: interactief magazijn → hotspot → uitdaging, oplossing,
 * voordelen en de film (flowchart 5 oktober). De vier hotspots staan ook als
 * tegel in de actiebalk; een keuze vliegt de camera naar de machine. De film
 * start sinds 21 oktober niet meer vanzelf maar met de filmknop: de bezoeker
 * kiest zelf of hij hem bekijkt.
 */
export function Automatisering({ terug }: { terug: ReactNode }) {
  const taal = useKiosk((s) => s.taal)
  const hotspotId = useKiosk((s) => s.hotspot)
  const kiesHotspot = useKiosk((s) => s.kiesHotspot)
  const vergrendeld = useKiosk((s) => s.vergrendeld)
  const toonProduct = useKiosk((s) => s.toonProduct)

  const module = MODULES.find((m) => m.id === 'automatisering')!
  const hotspot = HOTSPOTS.find((h) => h.id === hotspotId) ?? null
  // De machine in de scène rijdt; wie hem van dichtbij wil zien krijgt het
  // echte productmodel in een eigen viewer. De scène is sfeer, dit is het product.
  const product = hotspot ? productBijModel(hotspot.truck) : undefined


  return (
    <>
      <div className="kiosk-podium">
        <AnimatePresence mode="wait">
          {hotspot ? (
            <motion.div
              key={hotspot.id}
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 24 }}
              transition={VEER}
            >
              <div className="kiosk-tegel-nr">{hotspot.truck}</div>
              {/* Een trede kleiner dan de andere onderdeelkoppen: de hotspotkoppen
                  uit de flowchart zijn twee regels, en op --k-h1 liep het podium
                  daarmee tot op de eenheid vol (720/720, K-MATIC in NL). */}
              <h2 className="kiosk-hero kiosk-hero-s">{t(hotspot.kop, taal)}</h2>
              {/* Uitdaging eerst: de bezoeker moet zichzelf herkennen voordat
                  het antwoord komt. Dat is de volgorde uit de briefing. */}
              <p className="kiosk-onderkop">{t(hotspot.uitdaging, taal)}</p>
              <p className="kiosk-oplossing">{t(hotspot.oplossing, taal)}</p>
              <ul className="kiosk-voordelen">
                {hotspot.voordelen.map((v, i) => (
                  <motion.li
                    key={i}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ ...VEER, delay: 0.18 + i * 0.07 }}
                  >
                    {t(v, taal)}
                  </motion.li>
                ))}
              </ul>
              <motion.div
                className="kiosk-hotspot-acties"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ ...VEER, delay: 0.42 }}
              >
                <FilmKnop video={hotspot.video} />
                {product && (
                  <button className="kiosk-3d kiosk-3d-lijn" onClick={() => toonProduct(product.id)}>
                    {taal === 'nl' ? 'Bekijk in 3D' : 'View in 3D'}
                  </button>
                )}
                {/* De productsheet als QR naast de machine: scannen en op je
                    telefoon lezen, want een kiosk kan niets downloaden. */}
                {product?.sheet && <ProductsheetQR sheet={product.sheet} taal={taal} compact />}
              </motion.div>
            </motion.div>
          ) : (
            <motion.div
              key="intro"
              initial={{ opacity: 0, y: 22 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={VEER}
            >
              <div className="kiosk-tegel-nr">{module.nr}</div>
              <h2 className="kiosk-hero">{t(module.naam, taal)}</h2>
              <p className="kiosk-onderkop">{t(module.sub, taal)}</p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <div className="kiosk-actiebalk">
        {terug}
        {HOTSPOTS.map((h, i) => (
          <button
            key={h.id}
            className={`kiosk-tegel${hotspotId === h.id ? ' kiosk-aan' : ''}`}
            onClick={() => kiesHotspot(hotspotId === h.id ? null : h.id)}
            disabled={vergrendeld}
          >
            <span className="kiosk-tegel-nr">{String(i + 1).padStart(2, '0')}</span>
            <span className="kiosk-tegel-naam">{h.truck}</span>
            <span className="kiosk-tegel-sub">{t(h.kop, taal)}</span>
          </button>
        ))}
      </div>
    </>
  )
}
